import { MyError } from 'utils/MyError';
// 글의 수정, 삭제, 숨기기 에 관한 팝업과 api 요청을 담당

import { makeAutoObservable, runInAction } from 'mobx';
import { DELETE_POST, DO_RENOTIFY_POST, DO_TOGGLE_POST_VISIBILITY } from 'network/shared/post';
import BaseStore from 'stores/BaseStore';
import UserStore from 'stores/root/UserStore';
import { PostT } from 'types/shared/core/Post';
import { EmptyRequestT } from 'types/shared/reqAndRes/CommonRequest';
import { MyResponse } from 'types/shared/reqAndRes/MyResponse';
import { PostRenotifyRequestT } from 'types/shared/reqAndRes/Post';
import Co from 'constants/frontend/shared/Co';
import { ErrorCode } from 'constants/shared/ErrorCode';
import { api } from 'utils/api/api';
import interfaceBridge from 'utils/interfaceBridge';

export default class PostActionStore implements BaseStore {
  userStore: UserStore;
  isRenotifying = false;
  // 다시 알림은 상세 화면에서만 표시한다.
  showRenotify = false;

  togglePostVisibilityCallback: (post: PostT) => void;
  deletePostCallback: (post: PostT) => void;
  editPostCallback: (post: PostT) => void;

  constructor(
    userStore: UserStore,
    editPostCallback: (post: PostT) => void,
    togglePostVisibilityCallback: (post: PostT) => void,
    deletePostCallback: (post: PostT) => void,
  ) {
    this.userStore = userStore;
    this.togglePostVisibilityCallback = togglePostVisibilityCallback;
    this.deletePostCallback = deletePostCallback;
    this.editPostCallback = editPostCallback;

    makeAutoObservable(this);
  }

  handlePostCornerAction = (post: PostT) => {
    console.log('handlePostCornerAction', post);

    const possibleActions: {
      title: string;
      value: string;
      isDestructive?: boolean;
    }[] = [];
    if (post.writerId === this.userStore.myInfo?.userInfo.id) {
      possibleActions.push({ title: '수정', value: 'edit' });
    }
    if (
      post.writerId === this.userStore.myInfo?.userInfo.id ||
      this.userStore.isManager
    ) {
      if (post.isVisible) {
        possibleActions.push({
          title: '숨기기',
          value: 'hide',
        });
      } else {
        possibleActions.push({
          title: '숨기기 취소',
          value: 'show',
        });
      }

      if (this.showRenotify && (post.type === 'boardPost' || post.type === 'notePost')) {
        possibleActions.push({ title: '다시 알림', value: 'renotify' });
      }

      possibleActions.push({
        title: '삭제',
        value: 'delete',
        isDestructive: true,
      });
    }

    if (!possibleActions.length) return;

    interfaceBridge.actionSheet(
      '작업 선택',
      '해당 글에 대한 작업을 선택해 주세요.',
      possibleActions,
      (value: string) => {
        if (value === 'edit') {
          this.handleEdit(post);
        } else if (value === 'hide') {
          this.handlePostToggleVisibility(post, false);
        } else if (value === 'show') {
          this.handlePostToggleVisibility(post, true);
        } else if (value === 'delete') {
          this.handlePostDelete(post);
        } else if (value === 'renotify') {
          this.handlePostRenotify(post);
        }
      },
    );
  };

  handlePostRenotify = async (post: PostT) => {
    if (
      this.isRenotifying ||
      (post.type !== 'boardPost' && post.type !== 'notePost') ||
      (!this.userStore.isManager && post.writerId !== this.userStore.myInfo?.userInfo.id)
    ) {
      return;
    }

    if (!this.userStore.activeAcademySetting?.insiderSetting?.feaPostRenotify) {
      const showEvent = await interfaceBridge.customConfirm(
        '다시 알림',
        Co.Feature.plusPlanOrEventRequiredMessage,
        '이벤트 보기',
        '닫기',
      );
      if (showEvent) {
        interfaceBridge.nav('이벤트', Co.Post.postViewerEventUrl, 'external');
      }
      return;
    }

    if (!post.isVisible || !post.publishedAt) {
      await interfaceBridge.customAlert(
        '다시 알림',
        '공개된 발행 글만 다시 알림을 보낼 수 있습니다.',
        '닫기',
      );
      return;
    }

    this.isRenotifying = true;
    try {
      if (
        !interfaceBridge.customConfirm(
          '다시 알림',
          '이 글을 안 읽은 학생·학부모에게 알림을 다시 보내시겠습니까?',
          '안 읽은 사람 다시 알림',
          '취소',
        )
      ) {
        return;
      }

      const result = await api<PostRenotifyRequestT, MyResponse<void>>(
        DO_RENOTIFY_POST(0, post.type, post.id),
        { unreadOnly: true },
      );
      if (result.isSuccess) {
        await interfaceBridge.customAlert('다시 알림', '다시 알림을 요청했습니다.', '닫기');
      } else if (result.errorCode === ErrorCode.COMMON_FEATURE_NOT_ENABLED) {
        const showEvent = await interfaceBridge.customConfirm(
          '다시 알림',
          Co.Feature.plusPlanOrEventRequiredMessage,
          '이벤트 보기',
          '닫기',
        );
        if (showEvent) {
          interfaceBridge.nav('이벤트', Co.Post.postViewerEventUrl, 'external');
        }
      } else if (result.errorCode === ErrorCode.COMMON_UNEXPECTED_OBJECT_STATUS) {
        await interfaceBridge.customAlert(
          '다시 알림',
          '공개된 발행 글만 다시 알림을 보낼 수 있습니다.',
          '닫기',
        );
      } else {
        await interfaceBridge.customAlert(
          '다시 알림 실패',
          `오류가 발생했습니다. (${result.errorCode})`,
          '닫기',
        );
      }
    } catch (e) {
      await MyError.handleCatchedError(e);
    } finally {
      runInAction(() => {
        this.isRenotifying = false;
      });
    }
  };

  handleEdit = (post: PostT) => {
    console.log('handleEdit');

    if (post.type === 'boardPost') {
      interfaceBridge.nav(
        '게시글 수정',
        `/teacherSide/post/editBoardPost/${post.id}`,
        'cover',
        true,
        (jsonStr) => {
          const decodedData = jsonStr ? JSON.parse(jsonStr) : undefined;
          if (decodedData && decodedData.edited) {
            console.log('handleEdit end');
            this.editPostCallback(post);
          }
        },
      );
    } else {
      interfaceBridge.nav(
        '알림장 수정',
        `/teacherSide/post/editNotePost/${post.id}`,
        'cover',
        true,
        (jsonStr) => {
          console.log('teacher assign end', jsonStr);
          const decodedData = jsonStr ? JSON.parse(jsonStr) : undefined;
          console.log('teacher assign end', decodedData);
          if (decodedData && decodedData.edited) {
            this.editPostCallback(post);
          }
        },
      );
    }
  };

  handlePostToggleVisibility = async (post: PostT, newVisibility: boolean) => {
    console.log('post', post);
    if (newVisibility) {
      if (
        !interfaceBridge.customConfirm(
          '글 숨기기 취소',
          '계속하면 이 글을 학부모가 볼 수 있게 됩니다.',
          '확인',
          '취소',
        )
      ) {
        return;
      }
    } else {
      if (
        !interfaceBridge.customConfirm(
          '글 숨기기',
          '글을 숨기면 학부모는 더 이상 조회가 불가능합니다.',
          '확인',
          '취소',
        )
      ) {
        return;
      }
    }

    try {
      const result = await api<EmptyRequestT, MyResponse<boolean>>(
        DO_TOGGLE_POST_VISIBILITY(0, post.type, post.id),
        {},
      );
      runInAction(() => {
        if (result.isSuccess) {
          post.isVisible = !!result.object;
          this.togglePostVisibilityCallback(post);
        } else if (!result.isSuccess) {
          interfaceBridge.customAlert(
            '글 공개 설정 실패',
            `오류가 발생했습니다. (${result.errorCode})`,
            '닫기',
          );
        }
      });
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
  };

  handlePostDelete = async (post: PostT) => {
    if (
      !interfaceBridge.customConfirm(
        '글 삭제',
        '글을 삭제하시겠습니까? 복구는 불가능합니다.',
        '확인',
        '취소',
      )
    ) {
      return;
    }

    try {
      const result = await api<EmptyRequestT, MyResponse<void>>(
        DELETE_POST(0, post.type, post.id),
        {},
        // todo 삭제가 되었다면 피드라면 해당 피드 삭제, 글 보기라면 이 글에서 벗어나야 한다.
      );
      runInAction(() => {
        if (result.isSuccess) {
          this.deletePostCallback(post);
        } else if (!result.isSuccess) {
          interfaceBridge.customAlert(
            '글 삭제 실패',
            `오류가 발생했습니다. (${result.errorCode})`,
            '닫기',
          );
        }
      });
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
  };
}
